// The auth-aware controls that close a public page's header: Login (or Go to
// Dashboard), the chat launcher, and — once signed in — the account menu.
//
// This lives here rather than inside Home because the borrower page needs the
// SAME controls behaving the SAME way. Two hand-written copies would be two
// things to keep in step, and the auth states are exactly where a copy drifts:
// the resolving case renders nothing, the signed-in case swaps the first button
// AND grows a third control. One component, one set of rules.
//
// What it deliberately does NOT own is the journey wizard. Each page mounts its
// own and hands in `onTalk`, because WHERE it opens matters: the borrower page
// opens it over itself, so closing it returns the reader to the scroll position
// they left, rather than swapping the page out from under them first.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConsumerAuth } from '../features/auth/useConsumerAuth';

/**
 * @param {Function} onTalk  opens the journey picker on the host page. Called with
 *        NO arguments — pages wire report-style handlers that treat a first
 *        argument as a plan, and a click event is not a plan.
 */
/**
 * The same auth destinations, as rows for the header's mobile disclosure.
 *
 * Since 2026-09-26 (UX review P0) Login stays in the bar on phones too; only
 * below 360px, where the wordmark, Login, the primary action and the hamburger
 * cannot share one line, does it give way to these rows — see the .nav-cta
 * rules in theme.css. The rows are always here as well, one per kind of account.
 *
 * It lives in this file, next to the bar version, so the two can never disagree
 * about what "signed in" looks like — the auth states are exactly where a second
 * hand-written copy drifts. Both read the same hook.
 *
 * Not conditioned on width here: `.nav-more` is `display:none` above 820px, so
 * these rows are already unreachable on a desktop. One rule, one place.
 *
 * @param {Function} onNavigate  closes the disclosure — a menu that stays open
 *        behind the page it just opened reads as a control that did nothing.
 */
export function AuthMenuRows({ onNavigate, partnerLoginUrl }) {
  const nav = useNavigate();
  const { isAuthed, signOut, loading } = useConsumerAuth();

  // Same rule as the bar: until /me answers we know nothing about the BORROWER,
  // so we say nothing about them. The distributor row below does not depend on
  // that answer, so it is not held back by it.
  if (loading && !partnerLoginUrl) return null;

  const go = (fn) => { onNavigate?.(); fn(); };

  return (
    <>
      <div className="nav-more-sep" role="separator" />
      {loading ? null : isAuthed ? (
        <>
          <button onClick={() => go(() => nav('/dashboard'))}>Go to Dashboard</button>
          <button onClick={() => go(async () => { await signOut(); })}>↪ Log out</button>
        </>
      ) : (
        // 2026-09-26 (UX review, P0): one row per kind of account, since a
        // borrower and a distributor sign in to different apps.
        <button onClick={() => go(() => nav('/login'))}>Login as Borrower</button>
      )}
      {/* The staff-app login, like the Distributor plan buttons: shown whether
          or not a BORROWER is signed in here — that session is not the staff
          app's, so it must not hide the way in to it (2026-09-26). */}
      {partnerLoginUrl && (
        <a className="nav-more-link" href={partnerLoginUrl} onClick={() => onNavigate?.()}>
          Login as Distributor
        </a>
      )}
    </>
  );
}

/**
 * "Login ▾" for pages that belong to no one audience (the legal pages, pricing,
 * a general home page) — 2026-09-26, UX review P0. It asks which account before
 * sending anyone anywhere: borrowers sign in here (/login), distributors in the
 * staff app. A disclosure (button + menu), closed by an outside click, Escape,
 * or a choice.
 */
export function NavLoginMenu({ partnerLoginUrl }) {
  const nav = useNavigate();
  const { isAuthed, loading } = useConsumerAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  // Same rule as the Borrower bar (NavAuthCtas): until /me answers, say nothing
  // rather than tell a signed-in borrower they are signed out; once it does, a
  // signed-in borrower gets their way back in, not a Login. (The ☰ menu still
  // carries "Login as Distributor" for them — AuthMenuRows.)
  if (loading) return null;
  if (isAuthed) {
    return (
      <button type="button" className="btn btn-g btn-sm nav-cta-2nd" onClick={() => nav('/dashboard')}>
        Go to Dashboard
      </button>
    );
  }
  return (
    <div className="nav-login-menu" ref={ref}>
      <button
        type="button"
        className="btn btn-g btn-sm nav-login"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="nav-login-pop"
        onClick={() => setOpen((v) => !v)}
      >
        Login <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="nav-login-pop" id="nav-login-pop" role="menu">
          <button type="button" role="menuitem" onClick={() => { setOpen(false); nav('/login'); }}>
            <b>Borrower</b>
            <small>Your loans, offers and credit</small>
          </button>
          <a role="menuitem" href={partnerLoginUrl} onClick={() => setOpen(false)}>
            <b>Distributor</b>
            <small>Your partner console</small>
          </a>
        </div>
      )}
    </div>
  );
}

export default function NavAuthCtas({ onTalk }) {
  const nav = useNavigate();
  const { isAuthed, consumer, signOut, loading } = useConsumerAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close on outside click and on Escape — a disclosure the keyboard can open
  // must be one the keyboard can close.
  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div className="nav-cta">
      {/* Auth is resolved by an async /me. Until it answers we know NOTHING, so
          render NOTHING: showing "Login" would tell a logged-in borrower they're
          logged out, and a placeholder box just looks broken while it waits.
          The controls appear the moment we actually know. */}
      {loading ? null : isAuthed ? (
        <button className="btn btn-g btn-sm nav-cta-2nd" onClick={() => nav('/dashboard')}>
          Go to Dashboard
        </button>
      ) : (
        // 2026-09-26 (UX review, P0): nav-login, not nav-cta-2nd — the Login now
        // stays in the bar on phones (theme.css hid .nav-cta-2nd below 820px).
        <button className="btn btn-g btn-sm nav-login" onClick={() => nav('/login')}>Login</button>
      )}
      {/* Two labels, one button — the phone bar cannot hold the full label beside
          a wordmark and a hamburger. CSS shows exactly one (see .nav-talk-* in
          theme.css); `aria-label` keeps the full name for assistive tech in both
          cases, so what is announced never depends on the viewport. Opens the
          journey picker (Track / Compare / Vet / Rejected) — renamed to
          "Get Started" (2026-09-09) since it opens all four, not just tracking. */}
      <button
        className="btn btn-p btn-ing btn-sm nav-talk"
        onClick={() => onTalk()}
        aria-label="Get Started"
      >
        <span className="nav-talk-long">Get Started</span>
        <span className="nav-talk-short" aria-hidden="true">Get Started</span>
      </button>

      {/* Profile — the avatar IS the control. A topbar is wayfinding, not a place
          to publish the borrower's own phone number back at them; the number
          lives on the Profile page. Sits last, the conventional spot. */}
      {!loading && isAuthed && (
        <div className="user-menu" ref={menuRef}>
          <button
            className="user-chip"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Your account"
            title="Your account"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            {/* An initial, or a person glyph — never a placeholder letter and
                never the first DIGIT of the mobile, which is not an initial. */}
            <span className="user-av">
              {(consumer?.full_name || '').trim()
                ? consumer.full_name.trim()[0].toUpperCase()
                : '👤'}
            </span>
          </button>
          {menuOpen && (
            <div className="user-drop" role="menu">
              <button onClick={async () => { setMenuOpen(false); await signOut(); }}>↪ Log out</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
